import {makeAutoObservable, observable} from 'mobx'
import {Lang} from 'yougile-lang'
import {ArrUtil, SearchUtil} from 'yougile-util'
import {DataObjectId, Hub, Tag} from 'yougile-model'
import {Sync} from '@client/sync'
import {crmStore} from '@components/crm/crm-store'
import {MessageBox} from '@components/message-box'
import {getUserAccountName} from '@components/user-name'
import {createUseStore} from '@containers/hooks'
import {Sticker} from '@extensions/sticker/sticker'
import {getStickersLibrary, StickerLibrary} from './my-stickers-utils'

export type Tab = 'board' | 'library' | 'new' | 'creating' | 'editing'
export type NewStickerType = 'string' | 'sprint' | 'text' | 'numeric'

export interface MyStickersState {
  stickersLibrary: StickerLibrary[]
  boardStickers: Tag[]
  openEditor: boolean
  selectedSticker?: Tag
  draggedSticker?: Tag
  searchQuery: string
  currentTab: Tab
  previousTab: Tab
  orderIdxOrig?: DataObjectId[]
  newStickerType: NewStickerType | undefined
  stickerToRemove?: Tag
}

export class MyStickersStore {
  state: MyStickersState = {
    stickersLibrary: [],
    boardStickers: [],
    openEditor: false,
    searchQuery: '',
    currentTab: 'board',
    previousTab: 'board',
    newStickerType: undefined
  }

  constructor(currentTab?: Tab) {
    makeAutoObservable(this, {state: observable.shallow}, {autoBind: true})
    if (currentTab) {
      this.state.currentTab = currentTab
    }
  }

  sync(board: Hub): void {
    this.syncLibrary(board)
    this.syncStickers(board)

    Sticker.fixStickerStatesOrder(board)

    board.data.stickersSettings ??= {}
    const stickerIds = this.state.boardStickers.map(x => x.id)
    const order = board.data.stickersOrder ?? []
    if (!ArrUtil.shallowEqual(stickerIds, order)) {
      for (const stickerId of stickerIds) {
        board.data.stickersSettings[stickerId] ??= {}
      }
      Sticker.setStickerOrder(board, stickerIds)
    }
  }

  syncLibrary(board: Hub): void {
    const library = getStickersLibrary().filter(
      x =>
        !Sticker.isSystem(x.sticker) &&
        !Sticker.isSettingsInBoard(board, x.sticker)
    )
    this.setStickersLibrary(library)
  }

  syncStickers(board: Hub): void {
    const boardList = Sticker.list(board, {withSettings: true})
    const stickerIds = new Set(boardList.map(x => x.id))

    const box = Sync.getCurrentSandbox()
    const systemList = Sticker.list(box, {withSettings: true})
      .filter(x => Sticker.isSystem(x) && !stickerIds.has(x.id))
      .sort((a, b) => Sticker.getName(a).localeCompare(Sticker.getName(b)))

    const filteredList = [...boardList, ...systemList].filter(s => {
      return (
        !crmStore.isCrmBoard(board) ||
        crmStore.isStickerAllowedOnBoard(board, s)
      )
    })

    this.setBoardStickers(filteredList)
  }

  setBoardStickers(stickers: Tag[]): void {
    this.state.boardStickers = stickers
  }

  editSticker(stickerId: DataObjectId): void {
    const tag = Sync.getCurrentSandbox().getTag(stickerId)
    if (tag) {
      this.state.selectedSticker = tag
      this.setCurrentTab('editing')
    }
  }

  dragStart(sticker: Tag, board: Hub): void {
    const stickersOrder = board?.data?.stickersOrder
    const boardStickerIds = this.state.boardStickers.map(sticker => sticker.id)

    if (
      stickersOrder &&
      !ArrUtil.shallowEqual(stickersOrder, boardStickerIds)
    ) {
      Sticker.setStickerOrder(board, boardStickerIds)
    }

    this.state.draggedSticker = sticker
    this.state.orderIdxOrig = boardStickerIds
  }

  dragEnter(sticker: Tag): void {
    const draggedSticker = this.state.draggedSticker
    if (!draggedSticker || sticker.id === draggedSticker.id) {
      return
    }

    const stickers = this.state.boardStickers.slice()
    const ixDrag = stickers.findIndex(x => x.id === draggedSticker.id)
    const ixOver = stickers.findIndex(x => x.id === sticker.id)
    const [spliced] = stickers.splice(ixDrag, 1)
    stickers.splice(ixOver, 0, spliced)
    this.setBoardStickers(stickers)
  }

  dragEnd(board: Hub): void {
    const boardStickerIds = this.state.boardStickers.map(x => x.id)
    if (!ArrUtil.shallowEqual(boardStickerIds, this.state.orderIdxOrig)) {
      Sticker.setStickerOrder(board, boardStickerIds)
      MessageBox.show(Lang.msg.myStickers.stickerOrderHasChanged, 'success', {
        modern: true
      })
    }
    this.state.orderIdxOrig = undefined
    this.state.draggedSticker = undefined
  }

  setSearchQuery(value: string): void {
    this.state.searchQuery = value
  }

  get filteredStickers(): StickerLibrary[] {
    return this.state.stickersLibrary.filter(library =>
      SearchUtil.smartSearch(
        [
          Sticker.getName(library.sticker),
          library.tree.map(
            tree =>
              `${tree.project.title} (${tree.boards
                .map(board => board.title)
                .join(', ')})`
          ),
          getUserAccountName({id: library.sticker.data.by})
        ]
          .filter(Boolean)
          .join(' '),
        this.state.searchQuery
      )
    )
  }

  setStickersLibrary(list: StickerLibrary[]): void {
    this.state.stickersLibrary = list.filter(sticker => sticker.isVisible)
  }

  setCurrentTab(tab: Tab): void {
    this.state.previousTab = this.state.currentTab
    this.state.currentTab = tab
  }

  goBack(): void {
    this.state.currentTab = this.state.previousTab
  }

  startCreatingNewStickerWithType(type: NewStickerType): void {
    this.state.newStickerType = type
    this.setCurrentTab('creating')
  }

  openRemoveStickerPopup(tag: Tag): void {
    this.state.stickerToRemove = tag
  }

  closeRemoveStickerPopup(): void {
    this.state.stickerToRemove = undefined
  }
}

const [MyStickersContext, useMyStickersStore] = createUseStore(MyStickersStore)
export {MyStickersContext, useMyStickersStore}
